import React, { useState, useMemo, useEffect } from 'react';
import { parseUrls, exportToZip, exportToPdf } from './utils/stickerUtils';
import SettingsPanel from './components/SettingsPanel';
import PreviewPanel from './components/PreviewPanel';
import LinkGenerator from './components/LinkGenerator';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import pako from 'pako';

const DEFAULT_TEXT = 'https://heroes.by/delivery?cinema=minsk-city&hall=4-heroes&row=4&seat=8';
const DEFAULT_OPTIONS = {
  bgColor: '#ffffff',
  textColor: '#000000',
  qrColor: '#000000',
  qrBgColor: '#ffffff',
  dotsStyle: 'dots',
  logoUrl: 'https://static.tildacdn.biz/tild3036-3438-4236-b733-636237303666/logo_heroes_descript.svg',
  stickerWidth: 50,
  stickerHeight: 50,
  stickerRadius: 3,
  topTemplate: 'Закажи еду!',
  bottomTemplate: 'ряд {row} | место {seat}',
  fontSize: 46,
  fontFamily: 'Oswald',
  customFontUrl: null,
  customFontName: '',
  logoBgColor: '#000000',
  logoBgOpacity: 80,
  logoBgRadius: 50,
  logoSize: 30
};

// Хелпер для сжатия данных в URL
const compressData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    const compressed = pako.deflate(jsonString);
    const base64 = btoa(String.fromCharCode.apply(null, compressed))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return base64;
  } catch (e) {
    console.error("Compression error:", e);
    return null;
  }
};

// Хелпер для распаковки данных из URL
const decompressData = (base64) => {
  try {
    let normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
    while (normalized.length % 4) normalized += '=';
    
    const binary = atob(normalized);
    const charData = binary.split('').map(x => x.charCodeAt(0));
    const binData = new Uint8Array(charData);
    const decompressed = pako.inflate(binData, { to: 'string' });
    return JSON.parse(decompressed);
  } catch (e) {
    console.error("Decompression error:", e);
    return null;
  }
};

export default function App() {
  const [isReady, setIsReady] = useState(false);
  const [activeTab, setActiveTab] = useState('stickers'); // 'stickers' | 'links'
  
  const [textInput, setTextInput] = useState(DEFAULT_TEXT);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  // Инициализация при загрузке
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareData = params.get('s');
    
    if (shareData) {
      const decoded = decompressData(shareData);
      if (decoded) {
        if (decoded.t) setTextInput(decoded.t);
        if (decoded.o) setOptions(prev => ({ ...prev, ...decoded.o }));
      }
    }
    setIsReady(true);
  }, []);

  const parsedData = useMemo(() => parseUrls(textInput), [textInput]);
  const previewItem = parsedData.length > 0 ? parsedData[0] : null;

  const handleGenerate = async (exportFn, typeName) => {
    if (parsedData.length === 0) return;
    setIsGenerating(true);
    setProgress(0);
    setLoadingText(`ГЕНЕРАЦИЯ ${typeName}...`);
    try {
      await exportFn(parsedData, options, setProgress);
    } catch (error) {
      console.error('Ошибка:', error);
      alert('Ошибка при генерации.');
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const handleShare = () => {
    const compressed = compressData({ t: textInput, o: options });
    if (!compressed) return;

    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set('s', compressed);
    
    navigator.clipboard.writeText(url.toString()).then(() => {
      setIsCopied(true);
      window.history.replaceState({}, '', url.toString());
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const handleSendToStickers = (links) => {
    setTextInput(links);
    setActiveTab('stickers');
  };

  if (!isReady) return null;

  return (
    <div className="min-h-screen bg-mainBg text-textMain pb-12">
      <nav className="bg-panelBg border-b border-gray-800 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <img 
            src="https://static.tildacdn.biz/tild3036-3438-4236-b733-636237303666/logo_heroes_descript.svg" 
            alt="Heroes" 
            className="h-6"
          />
          <div className="h-4 w-px bg-gray-700"></div>
          <span className="text-[10px] font-bold bg-accent text-black px-1.5 py-0.5 rounded uppercase tracking-wider">
            Stickers V9
          </span>
        </div>

        {/* Табы */}
        <div className="flex bg-[#1a1a1a] p-1 rounded-lg border border-gray-800">
          <button
            onClick={() => setActiveTab('stickers')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${
              activeTab === 'stickers' ? 'bg-panelCard text-accent shadow' : 'text-gray-400 hover:text-white'
            }`}
          >
            Генератор наклеек
          </button>
          <button
            onClick={() => setActiveTab('links')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-all ${
              activeTab === 'links' ? 'bg-panelCard text-accent shadow' : 'text-gray-400 hover:text-white'
            }`}
          >
            Генератор ссылок
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        
        {/* Контент вкладки "Генератор наклеек" */}
        <div style={{ display: activeTab === 'stickers' ? 'block' : 'none' }}>
          <header className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-2xl font-black text-white uppercase tracking-wider flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiSettings} className="text-accent" />
              Параметры генерации
            </h2>
            
            <button
              onClick={handleShare}
              className={`flex items-center gap-2 py-2 px-6 rounded-xl font-bold transition-all text-sm border ${
                isCopied 
                  ? 'bg-green-500 text-black border-green-500' 
                  : 'bg-panelCard border-gray-700 hover:border-accent hover:text-accent text-white'
              }`}
            >
              {isCopied ? (
                <>
                  <SafeIcon icon={FiIcons.FiCheck} />
                  <span>Ссылка скопирована</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiShare2} />
                  <span>Поделиться настройками</span>
                </>
              )}
            </button>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 xl:col-span-8">
              <SettingsPanel
                textInput={textInput}
                setTextInput={setTextInput}
                options={options}
                setOptions={setOptions}
                parsedData={parsedData}
                onExportZip={() => handleGenerate(exportToZip, 'ZIP АРХИВА')}
                onExportPdf={() => handleGenerate(exportToPdf, 'PDF ДОКУМЕНТА')}
                isGenerating={isGenerating}
              />
            </div>
            
            <div className="lg:col-span-5 xl:col-span-4 h-full">
              <PreviewPanel previewItem={previewItem} options={options} />
            </div>
          </div>
        </div>

        {/* Контент вкладки "Генератор ссылок" */}
        <div style={{ display: activeTab === 'links' ? 'block' : 'none' }}>
          <header className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-2xl font-black text-white uppercase tracking-wider flex items-center gap-3">
              <SafeIcon icon={FiIcons.FiLink} className="text-accent" />
              Создание массовых ссылок
            </h2>
          </header>
          
          <LinkGenerator onSendToStickers={handleSendToStickers} />
        </div>

      </div>

      {/* Оверлей загрузки для стикеров */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-panelBg border border-gray-800 rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 border-4 border-gray-800 border-t-accent rounded-full animate-spin mx-auto mb-6"></div>
              <h3 className="text-lg font-bold text-white tracking-widest uppercase">{loadingText}</h3>
              <p className="text-textMuted text-xs mt-2 uppercase tracking-wide">Подготовка файлов...</p>
            </div>
            <div className="w-full bg-inputBg rounded-full h-2 mb-2 overflow-hidden border border-gray-800">
              <div 
                className="bg-accent h-full rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="text-right text-xs font-bold text-accent">{progress}%</div>
          </div>
        </div>
      )}
    </div>
  );
}